import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Observable, Subscription } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';

import { DropdownHttpService } from '@core/services/dropdown-http.service';
import { AppPermissionService } from '@core/services/app-permission.service';
import { BlacklistTemplate } from '@core/models/blacklist-template.model';
import { Pagination } from '@core/models/pagination.model';
import { BlacklistTemplateService } from '../../services/blacklist-template.service';
import { TimezoneDatePipe } from '@core/pipes/timezone-date.pipe';
import { LoadingBarService } from '@ngx-loading-bar/core';
import { Status } from '@core/enums/status.enum';
import { BlacklistTemplateSettingComponent } from '../blacklist-template-setting/blacklist-template-setting.component';

@Component({
  selector: 'kt-blacklist-template',
  templateUrl: './blacklist-template.component.html',
  styleUrls: ['./blacklist-template.component.scss'],
})
export class BlacklistTemplateComponent implements OnInit, OnDestroy {
  form!: FormGroup;

  dropdown = {
    statuses: this.dropdownHttpService.statuses,
    perPage: this.dropdownHttpService.perPage,
  };

  status = Status;

  items$!: Observable<BlacklistTemplate[]>;
  pagination: Pagination | null = null;

  pageSize = 30;
  page = 1;
  maxSize = 5;
  params = '';

  loading = false;
  clearBtnLoading = false;
  searchBtnLoading = false;
  dataLength = 0;

  canViewGameProviderBlacklistTemplate = false;
  canAddGameProviderBlacklistTemplate = false;
  canEditGameProviderBlacklistTemplate = false;
  canDuplicateGameProviderBlacklistTemplate = false;

  messages$ = this.blacklistTemplateService.messages$;

  private subs = new Subscription();
  private permissionSubs = new Subscription();

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      page: string;
    },
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<BlacklistTemplateComponent>,
    private dropdownHttpService: DropdownHttpService,
    private blacklistTemplateService: BlacklistTemplateService,
    private appPermissionService: AppPermissionService,
    private cdr: ChangeDetectorRef,
    private timeZoneDate: TimezoneDatePipe,
    private loadingBar: LoadingBarService,
  ) {}

  ngOnInit() {
    this.formInit();

    const apSub = this.appPermissionService.getAppPermissions().subscribe(appPermissions => {
      if (this.data.page == 'promotion') {
        this.canViewGameProviderBlacklistTemplate = appPermissions.view_promotion_gp_blacklist_template;
        this.canAddGameProviderBlacklistTemplate = appPermissions.add_promotion_gp_blacklist_template;
        this.canEditGameProviderBlacklistTemplate = appPermissions.edit_promotion_gp_blacklist_template;
        this.canDuplicateGameProviderBlacklistTemplate = appPermissions.duplicate_promotion_gp_blacklist_template;
      }
      if (this.data.page == 'rebate') {
        this.canViewGameProviderBlacklistTemplate = appPermissions.rebate_settings_view_gp_blacklist_template;
        this.canAddGameProviderBlacklistTemplate = appPermissions.rebate_settings_add_gp_blacklist_template;
        this.canEditGameProviderBlacklistTemplate = appPermissions.rebate_settings_edit_gp_blacklist_template;
        this.canDuplicateGameProviderBlacklistTemplate = appPermissions.rebate_settings_duplicate_gp_blacklist_template;
      }
      if (this.data.page == 'game') {
        this.canViewGameProviderBlacklistTemplate = appPermissions.game_providers_view_gp_blacklist_template;
        this.canAddGameProviderBlacklistTemplate = appPermissions.game_providers_add_gp_blacklist_template;
        this.canEditGameProviderBlacklistTemplate = appPermissions.game_providers_edit_gp_blacklist_template;
        this.canDuplicateGameProviderBlacklistTemplate = appPermissions.game_providers_duplicate_gp_blacklist_template;
      }
    });

    this.permissionSubs.add(apSub);

    this.pagination = this.blacklistTemplateService?.pagination;
    this.onSubmit();
  }

  ngOnDestroy(): void {
    this.permissionSubs.unsubscribe();
  }

  onCloseDialog() {
    this.dialogRef.close();
  }

  timezoneDate(date: any, format: any) {
    return this.timeZoneDate.transform(date, format);
  }

  private formInit() {
    this.form = new FormGroup({
      name: new FormControl(null),
      status: new FormControl(''),
    });
  }

  onClear() {
    this.form.patchValue({ name: null, status: '' });
    this.onSubmit(true);
  }

  onSubmit(clearSearch?: boolean) {
    this.loading = true;
    this.searchBtnLoading = !clearSearch;
    this.clearBtnLoading = !!clearSearch;

    const data = this.filterFormFields(this.form.value);

    if (data['status'] === '') data['status'] = null;

    this.params = Object.keys(data)
      .map(key => `${key}=${encodeURIComponent(data[key])}`)
      .join('&');

    const query = `?${this.params}${this.params ? '&' : ''}page=${1}&perPage=${this.pageSize}`;

    this.loadingBar.start();

    this.items$ = this.blacklistTemplateService.getList(query).pipe(
      tap(res => {
        this.page = 1;
        this.dataLength = res.length;
        this.pagination = this.blacklistTemplateService.pagination;
      }),
      finalize(() => {
        this.loading = false;
        this.clearBtnLoading = false;
        this.searchBtnLoading = false;
        this.loadingBar.complete();
        this.cdr.detectChanges();
      })
    );
  }

  onViewPageBy(page = 1) {
    this.loading = true;
    this.loadingBar.start();

    const params = this.params ? `${this.params}&` : '';
    const query = `?${params}page=${page}&perPage=${this.pageSize}`;

    this.items$ = this.blacklistTemplateService.getList(query).pipe(
      tap(res => {
        this.page = page;
        this.dataLength = res.length;
        this.pagination = this.blacklistTemplateService.pagination;
      }),
      finalize(() => {
        this.loading = false;
        this.loadingBar.complete();
        this.cdr.detectChanges();
      })
    );
  }

  onPerPage(event: any) {
    const size = Number(event?.target?.value || this.pageSize);
    this.pageSize = size;
    this.onViewPageBy(1);
  }

  onOpenDialog(type: string, template: BlacklistTemplate) {
    if (type === 'view' || type === 'edit' || type === 'duplicate') {
      const sub = this.blacklistTemplateService.getGameProviderById(template.id).pipe(
        tap(res => {
          this.openDialogBy(BlacklistTemplateSettingComponent, {
            mode: type, // view OR edit OR duplicate
            template,
            setting: res,
          });
        })
      ).subscribe();

      this.subs.add(sub);
      return;
    }

    if (type === 'create') {
      const sub = this.blacklistTemplateService.getGameProvider().pipe(
        tap(res => {
          this.openDialogBy(BlacklistTemplateSettingComponent, {
            mode: 'create',
            setting: res,
          });
        })
      ).subscribe();

      this.subs.add(sub);
    }
  }

  private openDialogBy(componentRef: any, data?: { mode?: any; template?: BlacklistTemplate; setting?: any }) {
    this.dialog.open(componentRef, {
      width: '1300px',
      data: {
        mode: data?.mode,
        template: data?.template,
        setting: data?.setting,
      },
      autoFocus: false
    }).afterClosed().subscribe((result) => {
      console.log(result)
      this.onViewPageBy(this.page || 1);
    });
  }

  private filterFormFields(formData: any) {
    const fields: any = {};
    Object.keys(formData).forEach(key => {
      if (formData[key] !== '' && formData[key] !== null && key !== 'defaultDate') {
        fields[key] = formData[key];
      }
    });
    return fields;
  }
}
